import type { CustomFormResult, ReportingWebhookResult } from '@/services/auth/types';
import type {
  ServicesApiCustomForm,
  ServicesApiFacebookLeadAdsForm,
  ServicesApiReportingWebhook,
} from '@/services/api/services-api';
import { CUSTOM_FORM_TYPE_LABELS, FACEBOOK_LEAD_ADS_FORM_TYPE } from '../constants';
import { filterBySearchTerms } from './search-filter';

// ─── Custom Forms ─────────────────────────────────────────────────────────────

/**
 * Maps a ServicesApiCustomForm to a CustomFormResult.
 * The edit URL follows the Journeys integrations/forms/:type/:id route.
 */
export function mapCustomFormToSearchResult(form: ServicesApiCustomForm, workspaceId: number): CustomFormResult {
  return {
    kind: 'custom_form',
    doc_id: `custom_form-${form.id}`,
    title: form.name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/forms/${form.form_type}/${form.id}`,
    other: {
      env_id: workspaceId,
      form_type: form.form_type,
      form_id: form.form_id,
      status: form.status,
      created_at: form.created_at > 0 ? form.created_at * 1000 : undefined,
      last_activity_at: form.last_activity_at > 0 ? form.last_activity_at * 1000 : undefined,
      html_form_url: form.html_form_url.length > 0 ? form.html_form_url : undefined,
    },
  };
}

export function mapFacebookLeadAdsFormToSearchResult(
  form: ServicesApiFacebookLeadAdsForm,
  workspaceId: number,
): CustomFormResult {
  return {
    kind: 'custom_form',
    doc_id: `${FACEBOOK_LEAD_ADS_FORM_TYPE}_form-${form.id}`,
    title: form.facebook_form_name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/forms/${FACEBOOK_LEAD_ADS_FORM_TYPE}/${form.id}`,
    other: {
      env_id: workspaceId,
      form_type: FACEBOOK_LEAD_ADS_FORM_TYPE,
      form_id: form.facebook_form_id,
      status: form.status,
      created_at: form.created_at > 0 ? form.created_at * 1000 : undefined,
      last_activity_at: form.last_activity_at > 0 ? form.last_activity_at * 1000 : undefined,
    },
  };
}

/**
 * Client-side filter for Facebook Lead Ads forms. Matches against name, form type slug, and configured label.
 */
export function filterFacebookLeadAdsForms(
  forms: ServicesApiFacebookLeadAdsForm[],
  query: string,
  cap: number,
): ServicesApiFacebookLeadAdsForm[] {
  const displayLabel = CUSTOM_FORM_TYPE_LABELS[FACEBOOK_LEAD_ADS_FORM_TYPE];
  return filterBySearchTerms(forms, query, {
    cap,
    getTerms: (form) => [FACEBOOK_LEAD_ADS_FORM_TYPE, displayLabel, form.facebook_form_name],
  });
}

/**
 * Client-side filter for custom forms. Matches against name, form_type slug, and configured label.
 */
export function filterCustomForms(forms: ServicesApiCustomForm[], query: string, cap: number): ServicesApiCustomForm[] {
  return filterBySearchTerms(forms, query, {
    cap,
    getTerms: (form) => [form.name, form.form_type, CUSTOM_FORM_TYPE_LABELS[form.form_type] ?? ''],
  });
}

// ─── Reporting Webhooks ───────────────────────────────────────────────────────

/**
 * Maps a ServicesApiReportingWebhook to a ReportingWebhookResult.
 * The edit URL follows the Journeys integrations/reporting-webhooks/:id route.
 */
export function mapReportingWebhookToSearchResult(
  webhook: ServicesApiReportingWebhook,
  workspaceId: number,
): ReportingWebhookResult {
  return {
    kind: 'reporting_webhook',
    doc_id: `reporting_webhook-${webhook.id}`,
    title: webhook.name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/reporting-webhooks/${webhook.id}`,
    other: {
      enabled: webhook.enabled,
      created_at: webhook.created_at > 0 ? webhook.created_at * 1000 : undefined,
    },
  };
}

/**
 * Client-side filter for reporting webhooks. Matches against name.
 */
export function filterReportingWebhooks(
  webhooks: ServicesApiReportingWebhook[],
  query: string,
  cap: number,
): ServicesApiReportingWebhook[] {
  return filterBySearchTerms(webhooks, query, {
    cap,
    getTerms: (webhook) => [webhook.name],
  });
}
